/**
 * Display helpers for the Pick'em surfaces: team names, game labels, spread
 * labels, a game's lock line and the outcome chip. Formatting only. Every
 * fact these read (lock state, outcome, points, the line) is a server field.
 *
 * No React, no clock reads: `nowMs` and the time zone come in.
 */
import { formatSpreadLabel } from "../pick/pickBoardModel";
import {
  formatCountdownLabel,
  formatKickoffLabel,
} from "../pick/pickDeadlineModel";
import type { typeTeamName } from "../pick/useTeamNames";
import {
  PICKEM_LOCKED_LABEL,
  PICKEM_OUTCOME_LABELS,
  pickemLocksAtLine,
  pickemLocksInLine,
} from "./pickemCopy";
import type { typePickemOutcome } from "./pickemTypes";

export type typePickemTeamView = {
  teamId: string;
  name: string;
  abbreviation: string;
  logoUrl: string | null;
};

/** A team's name, or a neutral stand-in while the team list loads. */
export function pickemTeam(
  teamsById: ReadonlyMap<string, typeTeamName>,
  teamId: string,
): typePickemTeamView {
  const team = teamsById.get(teamId);
  if (team === undefined) {
    return { teamId, name: "Team", abbreviation: "TBD", logoUrl: null };
  }
  return {
    teamId,
    name: team.name,
    abbreviation: team.abbreviation,
    logoUrl: team.logoUrl,
  };
}

/** "Bills at Dolphins". */
export function pickemGameLabel(input: {
  teamsById: ReadonlyMap<string, typeTeamName>;
  awayTeamId: string;
  homeTeamId: string;
}): string {
  const away = pickemTeam(input.teamsById, input.awayTeamId).name;
  const home = pickemTeam(input.teamsById, input.homeTeamId).name;
  return `${away} at ${home}`;
}

/** "BUF at MIA", for the tight rows on the Results tab. */
export function pickemShortGameLabel(input: {
  teamsById: ReadonlyMap<string, typeTeamName>;
  awayTeamId: string;
  homeTeamId: string;
}): string {
  const away = pickemTeam(input.teamsById, input.awayTeamId).abbreviation;
  const home = pickemTeam(input.teamsById, input.homeTeamId).abbreviation;
  return `${away} at ${home}`;
}

/** The line for one side, from the home line the server sends: "-3.5". */
export function pickemSideSpreadLabel(input: {
  homeLine: number | null | undefined;
  isHome: boolean;
}): string | null {
  return formatSpreadLabel(input);
}

/**
 * The line frozen on a saved pick: "+4.5". The server stores it from the
 * picked team's side already, so it must not be flipped again for an away
 * pick (ISSUE-1305).
 */
export function pickemPickLineLabel(
  spreadLine: number | null | undefined,
): string | null {
  return formatSpreadLabel({ homeLine: spreadLine, isHome: true });
}

/**
 * A game's lock line: "Locks Sun 1:00 PM" a day or more out, "Locks in 2h 5m"
 * inside a day, "Locked" once the server says so.
 */
export function pickemLockLine(input: {
  locksAt: string;
  isLocked: boolean;
  nowMs: number;
  timeZone: string;
}): string {
  if (input.isLocked) return PICKEM_LOCKED_LABEL;
  const remainingMs = Date.parse(input.locksAt) - input.nowMs;
  if (Number.isNaN(remainingMs)) return "";
  if (remainingMs > 0 && remainingMs < 86_400_000) {
    return pickemLocksInLine(formatCountdownLabel(remainingMs));
  }
  return pickemLocksAtLine(
    formatKickoffLabel({ isoInstant: input.locksAt, timeZone: input.timeZone }),
  );
}

export type typePickemTone = "win" | "loss" | "push" | "pending" | "none";

/**
 * Chip colors. Each tone also carries its own word, so the state never rests
 * on color alone.
 */
export const PICKEM_TONE_CLASS: Record<typePickemTone, string> = {
  win: "border border-success bg-success-tint text-ink",
  loss: "border border-error bg-error-tint text-ink",
  push: "border border-line bg-surface-sunken text-ink",
  pending: "border border-primary bg-primary-tint text-ink",
  none: "border border-line bg-surface text-ink-secondary",
};

export function pickemOutcomeLabel(outcome: typePickemOutcome): string {
  return PICKEM_OUTCOME_LABELS[outcome];
}
